import { motion } from "framer-motion";

/* Shared brand colors (from logo) */
const G = "#5BC084";
const T = "#33A6C9";
const O = "#E17D49";
const R = "#E46A40";

const floaty = (i = 0) => ({
  animate: { y: [0, -6, 0] },
  transition: { duration: 4 + i, repeat: Infinity, ease: "easeInOut", delay: i * 0.4 },
});

/* ---------------------------------------------------------------- Hero network */
export function NetworkHero({ className = "" }) {
  const nodes = [
    { x: 90, y: 70, r: 10, c: G },
    { x: 250, y: 40, r: 7, c: T },
    { x: 380, y: 110, r: 12, c: O },
    { x: 150, y: 180, r: 8, c: T },
    { x: 300, y: 210, r: 10, c: R },
    { x: 60, y: 250, r: 6, c: G },
    { x: 410, y: 250, r: 7, c: T },
    { x: 220, y: 300, r: 13, c: O },
  ];
  const edges = [
    [0, 1], [1, 2], [0, 3], [3, 4], [2, 4], [3, 5], [4, 6], [3, 7], [4, 7], [1, 3],
  ];
  return (
    <svg viewBox="0 0 470 360" className={className} role="img" aria-label="Abstract network of connected businesses">
      <defs>
        <linearGradient id="heroGrad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={G} />
          <stop offset="45%" stopColor={T} />
          <stop offset="100%" stopColor={O} />
        </linearGradient>
      </defs>
      {edges.map(([a, b], i) => (
        <motion.line
          key={i}
          x1={nodes[a].x} y1={nodes[a].y} x2={nodes[b].x} y2={nodes[b].y}
          stroke="url(#heroGrad)" strokeWidth="1.5" strokeOpacity="0.5"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: 0.5 }}
          transition={{ duration: 1.1, delay: 0.2 + i * 0.06, ease: "easeInOut" }}
        />
      ))}
      {nodes.map((n, i) => (
        <motion.g key={i} {...floaty(i % 4)}>
          <motion.circle
            cx={n.x} cy={n.y} r={n.r} fill={n.c}
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.3 + i * 0.08, ease: [0.22, 1, 0.36, 1] }}
          />
          <circle cx={n.x} cy={n.y} r={n.r + 6} fill="none" stroke={n.c} strokeOpacity="0.18" strokeWidth="1.5" />
        </motion.g>
      ))}
    </svg>
  );
}

/* ---------------------------------------------------------------- Feature group illustrations */
export function GroupIllustration({ variant = "connect", className = "" }) {
  const common = { className, viewBox: "0 0 320 240", role: "img" };
  if (variant === "connect") {
    return (
      <svg {...common} aria-label="Connect illustration">
        {[[80, 70, G], [230, 60, T], [160, 170, O]].map(([x, y, c], i) => (
          <g key={i}>
            <line x1="160" y1="120" x2={x} y2={y} stroke={c} strokeWidth="2" strokeOpacity="0.35" />
          </g>
        ))}
        <motion.circle cx="160" cy="120" r="26" fill="none" stroke={T} strokeWidth="2" {...floaty(0)} />
        {[[80, 70, G], [230, 60, T], [160, 170, O], [160, 120, R]].map(([x, y, c], i) => (
          <motion.circle key={i} cx={x} cy={y} r={i === 3 ? 12 : 16} fill={c} {...floaty(i)} />
        ))}
      </svg>
    );
  }
  if (variant === "discover") {
    return (
      <svg {...common} aria-label="Discover illustration">
        <motion.circle cx="140" cy="110" r="55" fill="none" stroke={T} strokeWidth="3" {...floaty(0)} />
        <motion.line x1="182" y1="152" x2="240" y2="205" stroke={O} strokeWidth="8" strokeLinecap="round" {...floaty(1)} />
        {[[120, 90, G], [160, 100, O], [130, 130, T]].map(([x, y, c], i) => (
          <circle key={i} cx={x} cy={y} r="7" fill={c} />
        ))}
      </svg>
    );
  }
  if (variant === "collaborate") {
    return (
      <svg {...common} aria-label="Collaborate illustration">
        <motion.rect x="60" y="80" width="90" height="90" rx="18" fill={G} fillOpacity="0.9" {...floaty(0)} />
        <motion.rect x="130" y="60" width="90" height="90" rx="18" fill={T} fillOpacity="0.85" {...floaty(1)} />
        <motion.rect x="180" y="110" width="80" height="80" rx="18" fill={O} fillOpacity="0.85" {...floaty(2)} />
      </svg>
    );
  }
  // grow
  return (
    <svg {...common} aria-label="Grow illustration">
      {[70, 130, 190, 250].map((x, i) => (
        <motion.rect
          key={i} x={x} y={200 - (i + 1) * 34} width="34" height={(i + 1) * 34} rx="8"
          fill={[G, T, O, R][i]}
          initial={{ scaleY: 0, originY: 1 }}
          whileInView={{ scaleY: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
          style={{ transformBox: "fill-box", transformOrigin: "bottom" }}
        />
      ))}
      <motion.path d="M60 150 L130 120 L190 90 L270 45" fill="none" stroke={R} strokeWidth="3" strokeLinecap="round"
        initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={{ once: true }} transition={{ duration: 1 }} />
    </svg>
  );
}

/* ---------------------------------------------------------------- Cross-sell diagram */
export function CrossSellDiagram({ className = "" }) {
  const items = [
    { label: "Marketing Agency", c: G, x: 60, y: 60 },
    { label: "Web Development", c: T, x: 250, y: 60 },
    { label: "Accounting Firm", c: O, x: 60, y: 190 },
    { label: "HR Consultancy", c: R, x: 250, y: 190 },
  ];
  return (
    <svg viewBox="0 0 420 300" className={className} role="img" aria-label="Cross-selling network of complementary businesses">
      {items.map((it, i) => (
        <line key={i} x1="210" y1="150" x2={it.x + 55} y2={it.y + 25} stroke={it.c} strokeWidth="2" strokeOpacity="0.4" />
      ))}
      <circle cx="210" cy="150" r="30" fill="none" stroke="#ffffff" strokeOpacity="0.25" strokeWidth="2" />
      <circle cx="210" cy="150" r="16" fill="#ffffff" fillOpacity="0.12" />
      {items.map((it, i) => (
        <motion.g key={i} {...floaty(i)}>
          <rect x={it.x} y={it.y} width="110" height="50" rx="12" fill={it.c} fillOpacity="0.16" stroke={it.c} strokeWidth="1.5" />
          <circle cx={it.x + 18} cy={it.y + 25} r="6" fill={it.c} />
        </motion.g>
      ))}
    </svg>
  );
}

/* ---------------------------------------------------------------- Events */
export function EventsIllustration({ className = "" }) {
  return (
    <svg viewBox="0 0 420 260" className={className} role="img" aria-label="Professional events illustration">
      <motion.rect x="40" y="60" width="150" height="150" rx="18" fill={T} fillOpacity="0.14" stroke={T} strokeWidth="1.5" {...floaty(0)} />
      <motion.rect x="150" y="40" width="150" height="150" rx="18" fill={O} fillOpacity="0.14" stroke={O} strokeWidth="1.5" {...floaty(1)} />
      <motion.rect x="250" y="80" width="130" height="130" rx="18" fill={G} fillOpacity="0.14" stroke={G} strokeWidth="1.5" {...floaty(2)} />
      {[[90, 130], [210, 110], [305, 140]].map(([x, y], i) => (
        <g key={i}>
          <circle cx={x} cy={y} r="10" fill={[T, O, G][i]} />
          <rect x={x - 22} y={y + 20} width="44" height="6" rx="3" fill={[T, O, G][i]} fillOpacity="0.4" />
        </g>
      ))}
    </svg>
  );
}

/* ---------------------------------------------------------------- Community */
export function CommunityIllustration({ className = "" }) {
  return (
    <svg viewBox="0 0 420 240" className={className} role="img" aria-label="Community discussions illustration">
      <motion.rect x="40" y="50" width="180" height="90" rx="18" fill={G} fillOpacity="0.16" stroke={G} strokeWidth="1.5" {...floaty(0)} />
      <motion.rect x="200" y="120" width="180" height="90" rx="18" fill={O} fillOpacity="0.16" stroke={O} strokeWidth="1.5" {...floaty(1)} />
      {[70, 100, 130].map((x, i) => <circle key={i} cx={x} cy={95} r="6" fill={G} />)}
      {[230, 260, 290].map((x, i) => <circle key={i} cx={x} cy={165} r="6" fill={O} />)}
    </svg>
  );
}

/* ---------------------------------------------------------------- Category glyph (resources) */
export function CategoryGlyph({ color = T, seed = 0, className = "" }) {
  const shapes = seed % 4;
  return (
    <svg viewBox="0 0 120 120" className={className} role="img" aria-hidden="true">
      <rect x="0" y="0" width="120" height="120" rx="20" fill={color} fillOpacity="0.1" />
      {shapes === 0 && <circle cx="60" cy="60" r="30" fill="none" stroke={color} strokeWidth="4" />}
      {shapes === 1 && <rect x="34" y="34" width="52" height="52" rx="12" fill={color} fillOpacity="0.7" />}
      {shapes === 2 && <path d="M40 80 L60 40 L80 80 Z" fill={color} fillOpacity="0.7" />}
      {shapes === 3 && (
        <g stroke={color} strokeWidth="4" fill="none">
          <line x1="40" y1="80" x2="40" y2="55" /><line x1="60" y1="80" x2="60" y2="42" /><line x1="80" y1="80" x2="80" y2="50" />
        </g>
      )}
    </svg>
  );
}

/* ---------------------------------------------------------------- Membership tiers diagram */
export function MembershipTiers({ className = "" }) {
  const tiers = [
    { label: "Free", w: 120, c: T },
    { label: "Standard", w: 180, c: O },
    { label: "Premium", w: 240, c: R },
  ];
  return (
    <svg viewBox="0 0 320 220" className={className} role="img" aria-label="Membership tiers grow with you">
      {tiers.map((t, i) => (
        <motion.g key={i}
          initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
          transition={{ duration: 0.6, delay: i * 0.14 }}>
          <rect x="40" y={40 + i * 58} width={t.w} height="42" rx="10" fill={t.c} fillOpacity="0.15" stroke={t.c} strokeWidth="1.5" />
          <circle cx="62" cy={61 + i * 58} r="7" fill={t.c} />
        </motion.g>
      ))}
    </svg>
  );
}
